'use client';

import CartModal from '@/components/cart/modal';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { menuData } from '@/data/menu';
import { ChevronRight, Menu } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

const ProductContent = ({ items, onItemClick }: { items: any[]; onItemClick: () => void }) => (
  <div className="space-y-2">
    {items.map((item) =>
      item.link ? (
        <Link
          key={item.id}
          href={item.link}
          className="flex items-center gap-3 rounded-lg p-2 hover:bg-gray-100"
          onClick={onItemClick}
        >
          <div className="flex h-12 w-12 items-center justify-center rounded border border-neutral-200 bg-white dark:border-neutral-800">
            <Image
              src={item.image}
              alt={item.name}
              width={40}
              height={40}
              className="object-contain"
            />
          </div>
          <div>
            <div className="font-medium">{item.name}</div>
            {item.specs && (
              <div className="text-sm text-neutral-500 dark:text-neutral-400">{item.specs}</div>
            )}
          </div>
        </Link>
      ) : (
        <div
          key={item.id}
          className="flex cursor-not-allowed items-center gap-3 rounded-lg p-2 opacity-60"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded border border-neutral-200 bg-white dark:border-neutral-800">
            <Image
              src={item.image}
              alt={item.name}
              width={40}
              height={40}
              className="object-contain"
            />
          </div>
          <div>
            <div className="font-medium">{item.name}</div>
            {item.specs && (
              <div className="text-sm text-neutral-500 dark:text-neutral-400">{item.specs}</div>
            )}
          </div>
        </div>
      )
    )}
  </div>
);

const ScenariosContent = ({ items, onItemClick }: { items: any[]; onItemClick: () => void }) => (
  <div className="flex flex-row gap-4">
    {items.map((item) =>
      item.link ? (
        <Link
          key={item.id}
          href={item.link}
          className="group relative h-40 w-44 overflow-hidden rounded-lg"
          onClick={onItemClick}
        >
          <Image src={item.image} alt={item.name} fill className="object-cover" />
          <div className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/30" />
          <div className="absolute bottom-2 left-2 font-medium text-white">{item.name}</div>
        </Link>
      ) : (
        <div
          key={item.id}
          className="group relative h-40 w-44 cursor-not-allowed overflow-hidden rounded-lg opacity-60"
        >
          <Image src={item.image} alt={item.name} fill className="object-cover" />
          <div className="absolute inset-0 bg-black/20 transition-colors group-hover:bg-black/30" />
          <div className="absolute bottom-2 left-2 font-medium text-white">{item.name}</div>
        </div>
      )
    )}
  </div>
);

const ListContent = ({ items, onItemClick }: { items: any[]; onItemClick: () => void }) => (
  <div className="space-y-2">
    {items.map((item) => (
      <Link
        key={item.name}
        href={item.link}
        className="block rounded-lg px-8 py-3 hover:bg-gray-100"
        onClick={onItemClick}
      >
        {item.name}
      </Link>
    ))}
  </div>
);

export function MobileMenu() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({});
  const [openCategories, setOpenCategories] = useState<{ [key: string]: boolean }>({});
  const [headerOffset, setHeaderOffset] = useState('7rem'); // Default offset

  // Calculate dynamic header offset
  useEffect(() => {
    const calculateHeaderOffset = () => {
      const promoBanner = document.querySelector('[data-component="promotional-banner"]') as HTMLElement;
      const header = document.getElementById('main-header') as HTMLElement;
      const currencyNavbar = document.querySelector('[data-component="currency-navbar"]') as HTMLElement;
      
      let totalOffset = 0;
      
      // Add promotional banner height ONLY if it's visible in viewport
      if (promoBanner && promoBanner.offsetHeight > 0) {
        const bannerRect = promoBanner.getBoundingClientRect();
        // Banner is visible if its bottom edge is above or at the top of viewport
        // and it's not completely scrolled past (top is not too negative)
        const isBannerVisible = bannerRect.bottom > 0 && bannerRect.top >= -bannerRect.height;
        
        if (isBannerVisible) {
          totalOffset += promoBanner.offsetHeight;
        }
      }
      
      // Add header height
      if (header) {
        totalOffset += header.offsetHeight;
      }
      
      // Add currency navbar height  
      if (currencyNavbar) {
        totalOffset += currencyNavbar.offsetHeight;
      }
      
      setHeaderOffset(`${totalOffset}px`);
    };

    // Calculate when mobile menu opens
    if (open) {
      calculateHeaderOffset();
    }
    
    // Also recalculate on scroll to handle banner visibility changes
    const handleScroll = () => {
      if (open) {
        calculateHeaderOffset();
      }
    };
    
    // Recalculate on window resize
    window.addEventListener('resize', calculateHeaderOffset);
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('resize', calculateHeaderOffset);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [open]); // Recalculate when menu opens/closes

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleCategory = (category: string) => {
    setOpenCategories((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleItemClick = () => setOpen(false);

  const renderContent = (category: any) => {
    if (category.type === 'multi-column' && category.items) {
      return <ScenariosContent items={category.items} onItemClick={handleItemClick} />;
    }

    if (category.type === 'nested' && category.subcategories) {
      return (
        <div className="space-y-4">
          {Object.entries(category.subcategories).map(([key, subcategory]: [string, any]) => (
            <div key={key}>
              <div className="mb-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">
                {subcategory.name}
              </div>
              <ProductContent items={subcategory.items || []} onItemClick={handleItemClick} />
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-4">
        <CartModal />
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" onClick={() => setOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
      </div>
      <SheetContent 
        side="top" 
        className="max-h-screen w-full overflow-y-auto"
        style={{ top: headerOffset }}
      >
        <SheetTitle>Menu</SheetTitle>
        <div className="flex flex-col">
          {/* Menu Items */}
          {Object.entries(menuData).map(([key, category]) => (
            <Collapsible key={key} open={openSections[key]} onOpenChange={() => toggleSection(key)}>
              <CollapsibleTrigger className="flex w-full items-center justify-between border-b border-menu-divider p-4">
                <span className="text-sm font-medium">{category.name}</span>
                <ChevronRight
                  className={`h-4 w-4 transition-transform duration-200 ${
                    openSections[key] ? 'rotate-90' : ''
                  }`}
                />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="bg-gray-50">
                  {/* List Content */}
                  {category.type === 'list' && category.items && (
                    <ListContent items={category.items} onItemClick={handleItemClick} />
                  )}
                  {/* Other Content */}
                  {category.subcategories &&
                    Object.entries(category.subcategories).map(([subKey, subcategory]) => (
                      <div key={subKey}>
                        <Collapsible
                          open={openCategories[subKey]}
                          onOpenChange={() => toggleCategory(subKey)}
                        >
                          <CollapsibleTrigger className="flex w-full items-center justify-between border-b border-menu-divider p-4">
                            <span className="text-sm text-neutral-500 dark:text-neutral-400">
                              {subcategory.name}
                            </span>
                            <ChevronRight
                              className={`h-4 w-4 transition-transform duration-200 ${
                                openCategories[subKey] ? 'rotate-90' : ''
                              }`}
                            />
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="space-y-4 p-4">{renderContent(subcategory)}</div>
                          </CollapsibleContent>
                        </Collapsible>
                      </div>
                    ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
